import React, { useEffect, useMemo, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import SignalWifiOffIcon from "@material-ui/icons/SignalWifiOff";
import SettingsInputAntennaIcon from "@material-ui/icons/SettingsInputAntenna";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import Avatar from "@material-ui/core/Avatar";
import Dialog from "@material-ui/core/Dialog";
import Paper from "@material-ui/core/Paper";
import Tooltip from "@material-ui/core/Tooltip";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { toast } from "react-toastify";

import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import { formatarTelefone, formatarNomeOuTelefone } from "../../utils/phone";
import { painelAstro } from "../../utils/estiloAstro";
import {
	APARELHOS,
	rotuloOrigem,
	quandoConsultou,
	textoPresenca,
	camposDoLeilao,
} from "../TicketInfo/infoCliente";

const drawerWidth = 380;

// Abas dos dados do cliente. O campo cai na primeira aba cujo padrao casar com
// o nome dele; o que nao casar com nenhuma vai para "Pessoal".
const ABAS = [
	{
		id: "comercial",
		rotulo: "Comercial",
		casa: /cpf|nascimento|prazo|parcela|valor|renda|ocupa|matr|banco|margem|empresa|cnpj|admiss|salario|salário|beneficio|benefício/i,
	},
	{
		id: "endereco",
		rotulo: "Endereço",
		casa: /endere|bairro|cidade|^uf$|estado|cep|numero|número|complemento|logradouro/i,
	},
	{ id: "pessoal", rotulo: "Pessoal", casa: null },
	// Dados vindos do Leilao (Sistema de Disparo). So aparece se o cliente veio de la.
	{ id: "leilao", rotulo: "Leilão", casa: null, so_se_tiver: true },
];

const STATUS_BANCO = {
	oferta: { rotulo: "Oferta", classe: "pOferta" },
	autorizar: { rotulo: "Falta autorizar", classe: "pAutorizar" },
	aguardando: { rotulo: "Aguardando", classe: "pAguardando" },
	sem_oferta: { rotulo: "Sem oferta", classe: "pNeutro" },
	nao_encontrado: { rotulo: "Não encontrado", classe: "pNeutro" },
	erro: { rotulo: "Erro", classe: "pErro" },
};

const moeda = (v) =>
	Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const abaDoCampo = (nome) => {
	const n = String(nome || "");
	const achou = ABAS.find((a) => a.casa && a.casa.test(n));
	return achou ? achou.id : "pessoal";
};

// Campo longo ocupa a linha inteira; o resto fica em duas colunas.
const ehLongo = (info) =>
	/m[aã]e|endere|logradouro|ocupa|empresa|empregador|bancos com oferta/i.test(info.name) ||
	String(info.name || "").length > 22 ||
	String(info.value || "").length > 18;

// Campos tecnicos que a IA grava para uso interno: os bancos aparecem
// organizados no bloco "Bancos consultados", entao o JSON cru fica de fora.
const campoOculto = (info) => /json/i.test(String(info?.name || ""));

const useStyles = makeStyles((theme) => {
	const escuro = theme.palette.type === "dark";
	const verde = escuro ? "#6ee7a0" : "#1E9855";
	return {
		drawer: {
			width: drawerWidth,
			flexShrink: 0,
			"@media (max-width: 600px)": { width: "100vw" },
		},
		drawerPaper: {
			width: drawerWidth,
			display: "flex",
			border: "none",
			borderLeft: `1px solid ${theme.palette.divider}`,
			"@media (max-width: 600px)": { width: "100vw" },
		},
		header: {
			display: "flex",
			gap: 6,
			borderBottom: `1px solid ${theme.palette.divider}`,
			backgroundColor: escuro ? "#1b2320" : "#ffffff",
			alignItems: "center",
			padding: theme.spacing(0, 1),
			minHeight: "72px",
			flexShrink: 0,
		},
		tituloHeader: {
			fontWeight: 700,
			fontSize: "0.98rem",
			color: theme.palette.text.primary,
		},
		content: {
			display: "flex",
			gap: 10,
			backgroundColor: escuro ? "#131917" : "#f3f7f4",
			flexDirection: "column",
			padding: 10,
			height: "100%",
			overflowY: "auto",
			...theme.scrollbarStyles,
			"& > *": { flexShrink: 0 },
		},

		// ---- cartao do topo ---------------------------------------------------
		cartao: {
			...painelAstro(theme),
			padding: 12,
			position: "relative",
		},
		topo: {
			display: "flex",
			gap: 12,
			alignItems: "center",
		},
		avatar: {
			width: 64,
			height: 64,
			flexShrink: 0,
			boxShadow: `0 0 0 3px ${theme.palette.background.paper}, 0 0 0 5px #22c55e`,
			background: "linear-gradient(135deg, #16a34a, #22c55e)",
		},
		avatarClicavel: { cursor: "zoom-in" },
		identidade: { minWidth: 0, flex: 1 },
		nome: {
			fontWeight: 800,
			fontSize: "0.95rem",
			lineHeight: 1.25,
			color: theme.palette.text.primary,
			display: "-webkit-box",
			WebkitLineClamp: 2,
			WebkitBoxOrient: "vertical",
			overflow: "hidden",
			paddingRight: 28,
		},
		telefone: {
			display: "inline-block",
			marginTop: 4,
			fontWeight: 700,
			fontSize: "0.8rem",
			"& a": { color: escuro ? "#86efac" : "#15803d" },
		},
		presenca: {
			display: "flex",
			alignItems: "center",
			gap: 5,
			marginTop: 2,
			fontSize: "0.74rem",
			color: theme.palette.text.secondary,
		},
		bolinha: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
		editar: {
			position: "absolute",
			top: 6,
			right: 6,
			padding: 6,
			color: verde,
		},
		conexao: {
			display: "inline-flex",
			alignItems: "center",
			gap: 5,
			marginTop: 10,
			padding: "3px 9px",
			borderRadius: 8,
			fontSize: "0.72rem",
			fontWeight: 600,
			maxWidth: "100%",
			overflow: "hidden",
			whiteSpace: "nowrap",
			textOverflow: "ellipsis",
		},
		conexaoOk: { backgroundColor: "rgba(30,152,85,0.12)", color: "#1E9855" },
		conexaoCaiu: { backgroundColor: "rgba(245,158,11,0.16)", color: "#b45309", fontWeight: 700 },
		conexaoAviso: { backgroundColor: "#dc2626", color: "#fff", fontWeight: 700 },
		atribuido: {
			display: "flex",
			alignItems: "center",
			gap: 6,
			marginTop: 8,
			fontSize: "0.78rem",
			color: theme.palette.text.secondary,
			minWidth: 0,
			"& b": { color: theme.palette.text.primary, fontWeight: 700 },
		},
		atribuidoTexto: {
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
			minWidth: 0,
		},
		recado: {
			marginTop: 10,
			padding: "6px 10px",
			borderRadius: 8,
			fontSize: "0.78rem",
			fontStyle: "italic",
			lineHeight: 1.35,
			color: theme.palette.text.secondary,
			backgroundColor: escuro ? "rgba(255,255,255,0.04)" : "#f5f7f6",
			wordBreak: "break-word",
		},

		// ---- resumo do WhatsApp ----------------------------------------------
		tituloSecao: {
			fontSize: "0.7rem",
			fontWeight: 700,
			letterSpacing: "0.06em",
			textTransform: "uppercase",
			color: theme.palette.text.secondary,
			margin: "0 0 8px 2px",
		},
		resumo: {
			display: "grid",
			gridTemplateColumns: "1fr 1fr",
			gap: 6,
		},
		item: {
			padding: "7px 10px",
			borderRadius: 8,
			backgroundColor: escuro ? "rgba(255,255,255,0.04)" : "#f5f7f6",
			minWidth: 0,
		},
		itemRotulo: {
			fontSize: "0.62rem",
			fontWeight: 600,
			letterSpacing: "0.04em",
			textTransform: "uppercase",
			color: theme.palette.text.secondary,
			marginBottom: 1,
		},
		itemValor: {
			fontSize: "0.82rem",
			fontWeight: 600,
			color: theme.palette.text.primary,
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
		},
		valorAzul: { color: "#1A73E8" },
		valorVermelho: { color: "#D93025" },
		itemAlerta: {
			gridColumn: "1 / -1",
			backgroundColor: "#D93025",
			borderColor: "#D93025",
			"& $itemRotulo, & $itemValor": { color: "#fff" },
		},

		// ---- abas + campos ----------------------------------------------------
		abas: {
			display: "flex",
			gap: 4,
			padding: 3,
			borderRadius: 10,
			marginBottom: 10,
			backgroundColor: escuro ? "rgba(255,255,255,0.05)" : "#eef4f0",
		},
		aba: {
			flex: 1,
			border: "none",
			cursor: "pointer",
			padding: "6px 4px",
			borderRadius: 8,
			fontSize: "0.74rem",
			fontWeight: 700,
			fontFamily: "inherit",
			color: theme.palette.text.secondary,
			backgroundColor: "transparent",
			transition: "background-color .15s ease, color .15s ease",
			"&:hover": { color: theme.palette.text.primary },
		},
		abaAtiva: {
			color: "#fff",
			backgroundColor: "#1E9855",
			"&:hover": { color: "#fff" },
		},
		contador: { opacity: 0.75, fontWeight: 600, marginLeft: 3 },
		campos: {
			display: "grid",
			gridTemplateColumns: "1fr 1fr",
			gridAutoFlow: "row dense",
			gap: 6,
		},
		campo: {
			padding: "7px 10px",
			borderRadius: 8,
			backgroundColor: escuro ? "rgba(255,255,255,0.04)" : "#f5f7f6",
			minWidth: 0,
			cursor: "copy",
			transition: "background-color .15s ease",
			"&:hover": { backgroundColor: escuro ? "rgba(255,255,255,0.08)" : "#edf1ef" },
		},
		campoLongo: { gridColumn: "1 / -1" },
		campoValor: {
			fontSize: "0.84rem",
			fontWeight: 600,
			lineHeight: 1.35,
			color: theme.palette.text.primary,
			whiteSpace: "normal",
			wordBreak: "break-word",
		},
		copiado: { color: verde, fontWeight: 700 },
		bancosTitulo: {
			display: "flex",
			alignItems: "baseline",
			justifyContent: "space-between",
			margin: "14px 2px 8px",
			fontSize: "0.7rem",
			fontWeight: 700,
			letterSpacing: "0.06em",
			textTransform: "uppercase",
			color: theme.palette.text.secondary,
			"& span": { textTransform: "none", letterSpacing: 0, fontWeight: 500 },
		},
		banco: {
			padding: "8px 10px",
			borderRadius: 8,
			backgroundColor: escuro ? "rgba(255,255,255,0.04)" : "#f5f7f6",
			marginBottom: 6,
		},
		bancoTopo: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			gap: 8,
		},
		bancoNome: {
			fontSize: "0.84rem",
			fontWeight: 700,
			color: theme.palette.text.primary,
		},
		pilula: {
			flexShrink: 0,
			fontSize: "0.62rem",
			fontWeight: 700,
			lineHeight: "16px",
			padding: "0 7px",
			borderRadius: 8,
		},
		pOferta: { color: "#137a43", backgroundColor: "rgba(30,152,85,0.14)" },
		pAutorizar: { color: "#a15c00", backgroundColor: "rgba(245,158,11,0.18)" },
		pAguardando: { color: "#1A73E8", backgroundColor: "rgba(26,115,232,0.12)" },
		pNeutro: {
			color: theme.palette.text.secondary,
			backgroundColor: escuro ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
		},
		pErro: { color: "#D93025", backgroundColor: "rgba(217,48,37,0.10)" },
		bancoValores: {
			marginTop: 3,
			fontSize: "0.8rem",
			color: theme.palette.text.primary,
			lineHeight: 1.4,
			"& b": { fontWeight: 700 },
		},
		bancoMsg: {
			marginTop: 3,
			fontSize: "0.74rem",
			color: theme.palette.text.secondary,
			lineHeight: 1.35,
			wordBreak: "break-word",
		},
		verPrazos: {
			marginTop: 5,
			border: "none",
			padding: 0,
			background: "none",
			cursor: "pointer",
			fontFamily: "inherit",
			fontSize: "0.72rem",
			fontWeight: 700,
			color: verde,
		},
		tabelaPrazos: {
			width: "100%",
			marginTop: 6,
			borderCollapse: "collapse",
			fontSize: "0.74rem",
			"& th": {
				textAlign: "left",
				fontWeight: 600,
				color: theme.palette.text.secondary,
				padding: "3px 4px",
				borderBottom: `1px solid ${theme.palette.divider}`,
			},
			"& td": {
				padding: "3px 4px",
				color: theme.palette.text.primary,
				borderBottom: `1px solid ${escuro ? "rgba(255,255,255,0.05)" : "#e9eeeb"}`,
			},
			"& tr:last-child td": { borderBottom: "none" },
		},
		linhaRecomendada: {
			"& td": { fontWeight: 700, color: escuro ? "#86efac" : "#137a43" },
		},
		botaoReenviar: {
			marginTop: 8,
			border: "none",
			cursor: "pointer",
			padding: "5px 12px",
			borderRadius: 7,
			fontSize: "0.74rem",
			fontWeight: 700,
			fontFamily: "inherit",
			color: "#D93025",
			backgroundColor: "#ffffff",
			"&:disabled": { opacity: 0.7, cursor: "default" },
		},
		vazio: {
			fontSize: "0.78rem",
			color: theme.palette.text.secondary,
			textAlign: "center",
			padding: "10px 0",
		},

		fotoAmpliada: {
			display: "block",
			maxWidth: "min(78vw, 520px)",
			maxHeight: "78vh",
			width: "auto",
			height: "auto",
			borderRadius: 12,
		},
	};
});

const ContactDrawer = ({
	open,
	handleDrawerClose,
	contact,
	ticketId,
	atendente,
	atribuidoPor,
	loading,
	conexaoNome,
	conexaoStatus,
	semConexao,
}) => {
	const classes = useStyles();

	const [modalOpen, setModalOpen] = useState(false);
	const [fotoAberta, setFotoAberta] = useState(false);
	const [info, setInfo] = useState(null);
	const [aba, setAba] = useState("comercial");
	const [copiado, setCopiado] = useState(null);
	const [reenviando, setReenviando] = useState(false);
	const [abertos, setAbertos] = useState({});

	const carregarInfo = () =>
		api
			.get(`/tickets/${ticketId}/info-cliente`)
			.then(({ data }) => setInfo(data))
			.catch(() => {});

	const reenviar = async () => {
		if (!ticketId || reenviando) return;
		setReenviando(true);
		try {
			const { data } = await api.post(`/tickets/${ticketId}/reenviar-presas`);
			if (data?.enviadas) {
				toast.success(`${data.enviadas} mensagem(ns) reenviada(s). Em alguns segundos os tiques devem mudar.`);
			} else {
				toast.info("Nada para reenviar (a conexão pode estar desconectada ou o cliente já recebeu).");
			}
			setTimeout(carregarInfo, 8000);
		} catch (err) {
			toast.error("Não foi possível reenviar agora.");
		}
		setReenviando(false);
	};

	// Mesmas informacoes do cabecalho, buscadas quando a barra abre.
	useEffect(() => {
		if (!open || !ticketId) return undefined;
		let ativo = true;
		api
			.get(`/tickets/${ticketId}/info-cliente`)
			.then(({ data }) => ativo && setInfo(data))
			.catch(() => {});
		return () => {
			ativo = false;
		};
	}, [open, ticketId]);

	useEffect(() => {
		setInfo(null);
	}, [ticketId]);

	const campos = useMemo(
		() =>
			(Array.isArray(contact?.extraInfo) ? contact.extraInfo : []).filter(
				(i) => String(i?.value || "").trim() && !campoOculto(i)
			),
		[contact]
	);

	const porAba = useMemo(() => {
		const grupos = { comercial: [], endereco: [], pessoal: [], leilao: camposDoLeilao(info?.leilao) };
		campos.forEach((c) => grupos[abaDoCampo(c.name)].push(c));
		return grupos;
	}, [campos, info]);

	// Se a aba escolhida estiver vazia, mostra a primeira que tiver dado.
	const abaVisivel = porAba[aba]?.length
		? aba
		: (ABAS.find((a) => porAba[a.id].length) || ABAS[0]).id;

	const copiar = (valor, chave) => {
		try {
			navigator.clipboard?.writeText(String(valor));
			setCopiado(chave);
			setTimeout(() => setCopiado((c) => (c === chave ? null : c)), 1200);
		} catch {
			/* sem clipboard */
		}
	};

	const pres = textoPresenca(
		info?.presenca && Date.now() - info.presenca.em < 30 * 60 * 1000 ? info.presenca : null,
		Date.now()
	);

	const resumo = [];
	if (info) {
		if (APARELHOS[info.aparelho]) {
			resumo.push({
				rotulo: "Aparelho",
				valor: APARELHOS[info.aparelho].rotulo,
				classe: info.aparelho === "iphone" ? classes.valorAzul : classes.valorVermelho,
			});
		}
		if (info.uf) resumo.push({ rotulo: "Região", valor: `${info.uf} · DDD ${info.ddd}` });
		resumo.push({ rotulo: "Origem", valor: rotuloOrigem(info) });
		resumo.push({ rotulo: "Conta", valor: info.business ? "WhatsApp Business" : "Pessoal" });
		resumo.push({
			rotulo: "Atendimentos",
			valor: info.atendimentos > 1 ? `${info.atendimentos}º atendimento` : "Primeiro",
		});
		resumo.push({
			rotulo: "Última consulta",
			valor: info.ultimaConsultaEm
				? quandoConsultou(info.ultimaConsultaEm, Date.now()).replace("consultou ", "")
				: "Nunca",
			dica: info.ultimaConsultaEm
				? `${new Date(info.ultimaConsultaEm).toLocaleString("pt-BR")}${
						info.totalConsultas ? ` · ${info.totalConsultas} consulta(s)` : ""
				  }`
				: undefined,
		});
		if (info.vistoDesligado) resumo.push({ rotulo: "Confirmação de leitura", valor: "Desligada" });
	}

	const classeConexao = semConexao
		? classes.conexaoAviso
		: conexaoStatus && conexaoStatus !== "CONNECTED"
		? classes.conexaoCaiu
		: classes.conexaoOk;

	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
			classes={{ paper: classes.drawerPaper }}
		>
			<div className={classes.header}>
				<IconButton onClick={handleDrawerClose}>
					<CloseIcon />
				</IconButton>
				<Typography className={classes.tituloHeader}>
					{i18n.t("contactDrawer.header")}
				</Typography>
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					{/* ---- quem e o cliente ---- */}
					<Paper square variant="outlined" className={classes.cartao}>
						<Tooltip title={i18n.t("contactDrawer.buttons.edit")}>
							<IconButton className={classes.editar} onClick={() => setModalOpen(true)}>
								<EditOutlinedIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<div className={classes.topo}>
							<Avatar
								alt={contact.name}
								src={contact.profilePicUrl}
								className={`${classes.avatar} ${contact.profilePicUrl ? classes.avatarClicavel : ""}`}
								onClick={() => contact.profilePicUrl && setFotoAberta(true)}
								title={contact.profilePicUrl ? "Ver foto ampliada" : ""}
							/>
							<div className={classes.identidade}>
								<div className={classes.nome}>
									{formatarNomeOuTelefone(contact?.name, contact?.number)}
								</div>
								<div className={classes.telefone}>
									<Link href={`tel:${contact.number}`}>{formatarTelefone(contact.number)}</Link>
								</div>
								{pres && (
									<div className={classes.presenca}>
										<span
											className={classes.bolinha}
											style={{ backgroundColor: pres.tipo === "offline" ? "#b8c0c8" : "#1E9855" }}
										/>
										<span style={pres.tipo !== "offline" ? { color: "#1E9855", fontWeight: 600 } : undefined}>
											{pres.texto}
										</span>
									</div>
								)}
							</div>
						</div>

						{/* Por qual numero nosso falamos com este cliente. */}
						{(semConexao || conexaoNome) && (
							<div
								className={`${classes.conexao} ${classeConexao}`}
								title={semConexao ? "Use “Trocar conexão” no menu do atendimento" : conexaoNome}
							>
								{semConexao ? (
									<SignalWifiOffIcon style={{ fontSize: 15 }} />
								) : (
									<SettingsInputAntennaIcon style={{ fontSize: 15 }} />
								)}
								<span>
									{semConexao
										? `Sem conexão${conexaoNome ? ` — era “${conexaoNome}”` : ""}`
										: `${conexaoNome}${conexaoStatus && conexaoStatus !== "CONNECTED" ? " — desconectado" : ""}`}
								</span>
							</div>
						)}

						{/* Quem esta com o cliente agora (e quem atribuiu). */}
						<div className={classes.atribuido}>
							<PersonOutlineIcon style={{ fontSize: 16, flexShrink: 0 }} />
							<span
								className={classes.atribuidoTexto}
								title={atendente ? `Atribuído à ${atendente}${atribuidoPor ? ` · por ${atribuidoPor}` : ""}` : undefined}
							>
								{atendente ? (
									<>
										Atribuído à <b>{atendente}</b>
										{atribuidoPor ? ` · por ${atribuidoPor}` : ""}
									</>
								) : (
									"Sem atendente (com a I.A ou na fila)"
								)}
							</span>
						</div>

						{info?.recado && (
							<div className={classes.recado} title="Recado do perfil do WhatsApp">
								“{info.recado}”
							</div>
						)}
					</Paper>

					{/* ---- resumo do WhatsApp ---- */}
					{info && (
						<Paper square variant="outlined" className={classes.cartao}>
							<Typography className={classes.tituloSecao}>WhatsApp</Typography>
							<div className={classes.resumo}>
								{info.naoEstaRecebendo && (
									<div className={`${classes.item} ${classes.itemAlerta}`}>
										<div className={classes.itemRotulo}>Entrega</div>
										<div className={classes.itemValor} style={{ whiteSpace: "normal" }}>
											Não está recebendo as mensagens
										</div>
										<button
											type="button"
											className={classes.botaoReenviar}
											disabled={reenviando}
											onClick={reenviar}
											title="Cria uma conexão nova com o celular do cliente e manda de novo o que ficou preso"
										>
											{reenviando ? "Reenviando..." : "Reenviar agora"}
										</button>
									</div>
								)}
								{resumo.map((r) => (
									<div key={r.rotulo} className={classes.item} title={r.dica || r.valor}>
										<div className={classes.itemRotulo}>{r.rotulo}</div>
										<div className={`${classes.itemValor} ${r.classe || ""}`}>{r.valor}</div>
									</div>
								))}
							</div>
						</Paper>
					)}

					{/* ---- dados do cliente, em abas ---- */}
					<Paper square variant="outlined" className={classes.cartao}>
						<div className={classes.abas}>
							{ABAS.filter((a) => !a.so_se_tiver || porAba[a.id].length).map((a) => (
								<button
									key={a.id}
									type="button"
									className={`${classes.aba} ${abaVisivel === a.id ? classes.abaAtiva : ""}`}
									onClick={() => setAba(a.id)}
								>
									{a.rotulo}
									<span className={classes.contador}>{porAba[a.id].length}</span>
								</button>
							))}
						</div>

						{porAba[abaVisivel].length ? (
							<div className={classes.campos}>
								{porAba[abaVisivel].map((c, i) => {
									const chave = `${c.id || c.name}-${i}`;
									return (
										<div
											key={chave}
											className={`${classes.campo} ${ehLongo(c) ? classes.campoLongo : ""}`}
											onClick={() => copiar(c.value, chave)}
											title={`${c.value}\n(clique para copiar)`}
										>
											<div className={classes.itemRotulo}>
												{c.name}
												{copiado === chave && <span className={classes.copiado}> · copiado</span>}
											</div>
											<div className={classes.campoValor}>{c.value}</div>
										</div>
									);
								})}
							</div>
						) : (
							<div className={classes.vazio}>Nenhum dado cadastrado ainda.</div>
						)}

						{abaVisivel === "comercial" && info?.bancos?.bancos?.length > 0 && (
							<>
								<div className={classes.bancosTitulo}>
									Bancos consultados
									{info.bancos.consultadoEm && (
										<span>{new Date(info.bancos.consultadoEm).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
									)}
								</div>
								{info.bancos.bancos.map((b, idx) => {
									const st = STATUS_BANCO[b.status] || STATUS_BANCO.erro;
									const primeiraOferta = info.bancos.bancos.findIndex((x) => x.status === "oferta");
									const aberto = abertos[b.chave] ?? idx === primeiraOferta;
									return (
										<div key={b.chave} className={classes.banco}>
											<div className={classes.bancoTopo}>
												<span className={classes.bancoNome}>{b.banco}</span>
												<span className={`${classes.pilula} ${classes[st.classe]}`}>{st.rotulo}</span>
											</div>
											{(b.valor || b.parcela || b.margem) && (
												<div className={classes.bancoValores}>
													{b.valor ? <b>{moeda(b.valor)}</b> : null}
													{b.parcela ? ` · ${b.prazo ? `${b.prazo}x de ` : "parcela "}${moeda(b.parcela)}` : b.prazo ? ` · ${b.prazo}x` : ""}
													{b.margem ? `${b.valor || b.parcela ? " · " : ""}margem ${moeda(b.margem)}` : ""}
												</div>
											)}
											{b.mensagem && <div className={classes.bancoMsg}>{b.mensagem}</div>}
											{b.condicoes?.length > 0 && (
												<>
													<button
														type="button"
														className={classes.verPrazos}
														onClick={() => setAbertos((a) => ({ ...a, [b.chave]: !aberto }))}
													>
														{aberto ? "Ocultar prazos" : `Ver prazos (${b.condicoes.length})`}
													</button>
													{aberto && (
														<table className={classes.tabelaPrazos}>
															<thead>
																<tr>
																	<th>Prazo</th>
																	<th>Parcela</th>
																	<th>Valor liberado</th>
																</tr>
															</thead>
															<tbody>
																{b.condicoes.map((c, i) => (
																	<tr
																		key={`${c.prazo}-${i}`}
																		className={c.recomendada ? classes.linhaRecomendada : ""}
																		title={[c.tabela, c.recomendada ? "condição recomendada/apresentada" : ""].filter(Boolean).join(" · ")}
																	>
																		<td>{c.prazo ? `${c.prazo}x` : "—"}</td>
																		<td>{c.parcela ? moeda(c.parcela) : "—"}</td>
																		<td>{c.valor ? moeda(c.valor) : "—"}</td>
																	</tr>
																))}
															</tbody>
														</table>
													)}
												</>
											)}
										</div>
									);
								})}
							</>
						)}
					</Paper>

					<ContactModal
						open={modalOpen}
						onClose={() => setModalOpen(false)}
						contactId={contact.id}
						initialValues={contact}
					/>
				</div>
			)}

			<Dialog
				open={fotoAberta}
				onClose={() => setFotoAberta(false)}
				maxWidth={false}
				PaperProps={{ style: { backgroundColor: "transparent", boxShadow: "none", overflow: "visible" } }}
			>
				<img
					src={contact.profilePicUrl}
					alt={contact.name}
					className={classes.fotoAmpliada}
					onClick={() => setFotoAberta(false)}
					style={{ cursor: "zoom-out" }}
				/>
			</Dialog>
		</Drawer>
	);
};

export default ContactDrawer;
